import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, HelpCircle, Bot, Zap, Shield, Users, Clock, Headphones, XCircle } from 'lucide-react';

const UniversalFAQ = () => {
  const { t } = useTranslation();
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      id: 1,
      icon: <Bot className="w-5 h-5" />,
      questionKey: 'universalFAQ.questions.models.question',
      answerKey: 'universalFAQ.questions.models.answer',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 2,
      icon: <Zap className="w-5 h-5" />,
      questionKey: 'universalFAQ.questions.newModels.question',
      answerKey: 'universalFAQ.questions.newModels.answer',
      gradient: 'from-purple-500 to-violet-500'
    },
    {
      id: 3,
      icon: <Shield className="w-5 h-5" />,
      questionKey: 'universalFAQ.questions.quality.question',
      answerKey: 'universalFAQ.questions.quality.answer',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      id: 4,
      icon: <Users className="w-5 h-5" />,
      questionKey: 'universalFAQ.questions.techSavvy.question',
      answerKey: 'universalFAQ.questions.techSavvy.answer',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      id: 5,
      icon: <Clock className="w-5 h-5" />,
      questionKey: 'universalFAQ.questions.results.question',
      answerKey: 'universalFAQ.questions.results.answer',
      gradient: 'from-pink-500 to-rose-500'
    },
    {
      id: 6,
      icon: <Headphones className="w-5 h-5" />,
      questionKey: 'universalFAQ.questions.help.question',
      answerKey: 'universalFAQ.questions.help.answer',
      gradient: 'from-indigo-500 to-blue-500'
    },
    {
      id: 7,
      icon: <XCircle className="w-5 h-5" />,
      questionKey: 'universalFAQ.questions.cancel.question',
      answerKey: 'universalFAQ.questions.cancel.answer',
      gradient: 'from-gray-500 to-slate-500'
    }
  ];

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-amber-50/50 via-white to-yellow-50/50 relative overflow-hidden">
      {/* 装饰性背景元素 */}
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-100 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute top-40 -left-32 w-64 h-64 bg-yellow-100 rounded-full opacity-15 blur-2xl"></div>
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* 标题部分 */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-600 rounded-full px-6 py-3 text-sm font-semibold mb-8">
            <HelpCircle className="w-4 h-4 mr-2" />
            {t('universalFAQ.badge')}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {t('universalFAQ.headline')}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('universalFAQ.subtitle')}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl"
            >
              {/* Question Header */}
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 bg-gradient-to-r ${faq.gradient} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                    {faq.icon}
                  </div>
                  <h3 className="text-base font-bold text-gray-900 pr-4">
                    {t(faq.questionKey)}
                  </h3>
                </div>
                <div className="flex-shrink-0">
                  {openFAQ === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </button>

              {/* Answer Content */}
              {openFAQ === faq.id && (
                <div className="px-6 pb-6">
                  <div className="ml-14 pt-2">
                    <div className={`h-1 w-12 bg-gradient-to-r ${faq.gradient} rounded-full mb-4`}></div>
                    <p className="text-gray-700 leading-relaxed text-sm">
                      {t(faq.answerKey)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4 text-sm">
            {t('universalFAQ.stillHaveQuestions')}
          </p>
          <button className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-yellow-600 text-white px-6 py-3 rounded-2xl font-semibold hover:scale-105 transition-transform duration-300 shadow-lg">
            <HelpCircle className="w-4 h-4" />
            {t('universalFAQ.contactSupport')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default UniversalFAQ;